import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs';
import { GaugeCircle, AlertCircle, RotateCcw, Calculator, Info } from 'lucide-react';
import {
  ComposedChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend,
  ResponsiveContainer, ReferenceLine, ReferenceArea,
} from 'recharts';
import {
  DEFAULT_INPUTS, MODES, POWER_UNITS, TORQUE_UNITS, POWER_SPEC, RPM_SPEC, TORQUE_SPEC,
  MODE_FIELD_SPECS, validateField,
} from '../../lib/torquePowerRpmTools';
import { torquePowerRpmToolApi } from '../../lib/toolsApi';
import {
  SERIES_COLORS, CHART_GRID, CHART_AXIS, AXIS_TICK_STYLE, AXIS_LABEL_STYLE, LINE_PROPS,
  TOOLTIP_CONTENT_STYLE, TOOLTIP_LABEL_STYLE, LEGEND_STYLE,
} from './simulator/chartTheme';
import ChartZoomHeader from './simulator/ChartZoomHeader';
import { useChartZoom } from './simulator/useChartZoom';
import ReferenceLineTopLabel, { REFERENCE_LABEL_TOP_MARGIN, referenceLabelAnchorFor } from './simulator/ReferenceLineTopLabel';

const CALC_DEBOUNCE_MS = 350;
const CHART_POINTS = 40;

const fmt = (n, d = 2) => (n === null || n === undefined || Number.isNaN(n) ? '—' : Number(n).toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d }));
const round2 = (n) => Math.round(n * 100) / 100;

const FieldLabel = ({ children, unit, htmlFor }) => (
  <Label htmlFor={htmlFor} className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
    {children} {unit && <span className="normal-case text-muted-foreground/70">({unit})</span>}
  </Label>
);

const TorquePowerRpmCalculator = () => {
  const [mode, setMode] = useState('torque');
  const [powerUnit, setPowerUnit] = useState(DEFAULT_INPUTS.powerUnit);
  const [torqueUnit, setTorqueUnit] = useState(DEFAULT_INPUTS.torqueUnit);
  const [raw, setRaw] = useState({
    powerValue: String(DEFAULT_INPUTS.powerValue),
    rpm: String(DEFAULT_INPUTS.rpm),
    torqueValue: String(DEFAULT_INPUTS.torqueValue),
  });
  const setField = (key, value) => setRaw((prev) => ({ ...prev, [key]: value }));

  const [results, setResults] = useState(null);
  const [calcError, setCalcError] = useState(null);
  const requestIdRef = useRef(0);

  const { fieldErrors, hasErrors } = useMemo(() => {
    const specs = MODE_FIELD_SPECS[mode];
    const errors = {};
    specs.forEach((spec) => {
      const err = validateField(spec, raw[spec.key]);
      if (err) errors[spec.key] = err;
    });
    return { fieldErrors: errors, hasErrors: Object.keys(errors).length > 0 };
  }, [mode, raw]);

  const runCalculation = () => {
    if (hasErrors) {
      setResults(null);
      setCalcError(null);
      return;
    }
    const requestId = ++requestIdRef.current;
    const payload = {
      mode,
      powerValue: raw.powerValue === '' ? undefined : Number(raw.powerValue),
      powerUnit,
      rpm: raw.rpm === '' ? undefined : Number(raw.rpm),
      torqueValue: raw.torqueValue === '' ? undefined : Number(raw.torqueValue),
      torqueUnit,
    };
    torquePowerRpmToolApi.calculate(payload)
      .then((outcome) => {
        if (requestId !== requestIdRef.current) return;
        if (outcome.errors) {
          setResults(null);
          setCalcError(Object.values(outcome.errors)[0]);
        } else {
          setResults(outcome.results);
          setCalcError(null);
        }
      })
      .catch((e) => {
        if (requestId === requestIdRef.current) setCalcError(e.message);
      });
  };

  useEffect(() => {
    if (hasErrors) {
      setResults(null);
      setCalcError(null);
      return;
    }
    const timer = setTimeout(runCalculation, CALC_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, raw, powerUnit, torqueUnit, hasErrors]);

  const handleReset = () => {
    setMode('torque');
    setPowerUnit(DEFAULT_INPUTS.powerUnit);
    setTorqueUnit(DEFAULT_INPUTS.torqueUnit);
    setRaw({
      powerValue: String(DEFAULT_INPUTS.powerValue),
      rpm: String(DEFAULT_INPUTS.rpm),
      torqueValue: String(DEFAULT_INPUTS.torqueValue),
    });
  };

  const chartData = useMemo(() => {
    if (!results) return [];
    const { powerKw, rpm: opRpm } = results;
    const rpmLow = Math.max(opRpm * 0.1, 1);
    const rpmHigh = opRpm * 2;
    const rows = [];
    for (let i = 0; i <= CHART_POINTS; i++) {
      const r = rpmLow + ((rpmHigh - rpmLow) * i) / CHART_POINTS;
      rows.push({
        rpm: Math.round(r),
        torqueNm: round2((powerKw * 9550) / r),
        powerKw: round2(powerKw),
      });
    }
    return rows;
  }, [results]);

  const numField = (key, spec) => {
    const error = fieldErrors[key];
    const id = `torque-power-rpm-${key}`;
    return (
      <div className="space-y-2" key={key}>
        <FieldLabel htmlFor={id} unit={spec.unit}>{spec.label}</FieldLabel>
        <Input
          id={id}
          type="number"
          step="any"
          value={raw[key]}
          onChange={(e) => setField(key, e.target.value)}
          className="font-mono"
          aria-invalid={!!error}
          aria-describedby={error ? `${id}-error` : undefined}
          data-testid={`${id}-input`}
        />
        {error && (
          <p id={`${id}-error`} className="flex items-center gap-1 text-xs text-destructive" data-testid={`${id}-error`} role="alert">
            <AlertCircle className="h-3 w-3 shrink-0" /> {error}
          </p>
        )}
      </div>
    );
  };

  const powerInput = (
    <>
      {numField('powerValue', POWER_SPEC)}
      <div className="space-y-2">
        <FieldLabel>Power Unit</FieldLabel>
        <Select value={powerUnit} onValueChange={setPowerUnit}>
          <SelectTrigger data-testid="torque-power-rpm-power-unit-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {POWER_UNITS.map((u) => <SelectItem key={u.value} value={u.value}>{u.name} ({u.label})</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
    </>
  );

  const torqueInput = (
    <>
      {numField('torqueValue', TORQUE_SPEC)}
      <div className="space-y-2">
        <FieldLabel>Torque Unit</FieldLabel>
        <Select value={torqueUnit} onValueChange={setTorqueUnit}>
          <SelectTrigger data-testid="torque-power-rpm-torque-unit-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {TORQUE_UNITS.map((u) => <SelectItem key={u.value} value={u.value}>{u.name} ({u.label})</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
    </>
  );

  const rpmInput = numField('rpm', RPM_SPEC);

  const activeMode = MODES.find((m) => m.id === mode) || MODES[0];

  return (
    <div className="space-y-6">
      <Card className="technical-card">
        <CardHeader>
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <CardTitle className="font-chivo uppercase tracking-tight flex items-center gap-2">
                <GaugeCircle className="h-5 w-5 text-autoxium-blue" />
                Torque-Power-RPM Calculator
              </CardTitle>
              <CardDescription>
                Calculate any one of Torque, RPM, or Power from the other two. Results update
                live as you type, with automatic unit conversion.
              </CardDescription>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button type="button" variant="default" size="sm" onClick={runCalculation} disabled={hasErrors} data-testid="torque-power-rpm-calculate-btn">
                <Calculator className="h-3.5 w-3.5" /> Calculate
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={handleReset} data-testid="torque-power-rpm-reset-btn">
                <RotateCcw className="h-3.5 w-3.5" /> Reset
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs value={mode} onValueChange={setMode}>
            <TabsList className="flex-wrap h-auto" data-testid="torque-power-rpm-mode-tabs">
              {MODES.map((m) => (
                <TabsTrigger key={m.id} value={m.id} data-testid={`torque-power-rpm-mode-${m.id}`}>{m.label}</TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </CardContent>
      </Card>

      <div className="grid lg:grid-cols-5 gap-6">
        <Card className="technical-card lg:col-span-2">
          <CardHeader>
            <CardTitle className="font-chivo uppercase tracking-tight text-base">Inputs</CardTitle>
            <CardDescription>{activeMode.description}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4 p-4 bg-muted/50 rounded-md">
              <div className="grid sm:grid-cols-2 gap-4">
                {mode === 'torque' && (<>{powerInput}{rpmInput}</>)}
                {mode === 'rpm' && (<>{powerInput}{torqueInput}</>)}
                {mode === 'power' && (<>{torqueInput}{rpmInput}</>)}
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="lg:col-span-3 space-y-4">
          <Card className="technical-card ring-1 ring-autoxium-blue/30" data-testid="torque-power-rpm-results">
            <CardHeader>
              <CardTitle className="font-chivo uppercase tracking-tight text-base">Result</CardTitle>
            </CardHeader>
            <CardContent>
              <div role="status" aria-live="polite">
                {hasErrors ? (
                  <p className="text-sm text-muted-foreground" data-testid="torque-power-rpm-results-placeholder">
                    Fix the highlighted field to see results.
                  </p>
                ) : calcError ? (
                  <div className="flex items-center gap-2 text-sm text-destructive" data-testid="torque-power-rpm-calc-error">
                    <AlertCircle className="h-4 w-4 shrink-0" /> {calcError}
                  </div>
                ) : !results ? (
                  <p className="text-sm text-muted-foreground" data-testid="torque-power-rpm-results-loading">
                    Calculating…
                  </p>
                ) : mode === 'torque' ? (
                  <div className="space-y-3">
                    <div className="p-4 rounded-md border border-autoxium-blue bg-autoxium-blue/5">
                      <p className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground mb-1">Torque</p>
                      <p className="font-mono font-black text-3xl text-autoxium-blue">{fmt(results.torque.nm)} <span className="text-base font-normal text-muted-foreground">N·m</span></p>
                    </div>
                    <div className="p-3 rounded-md border border-structure bg-muted/30">
                      <p className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground mb-1">Torque (lb-ft)</p>
                      <p className="font-mono font-bold text-lg">{fmt(results.torque.lbft)} <span className="text-xs font-normal text-muted-foreground">lb-ft</span></p>
                    </div>
                  </div>
                ) : mode === 'rpm' ? (
                  <div className="p-4 rounded-md border border-autoxium-blue bg-autoxium-blue/5">
                    <p className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground mb-1">RPM</p>
                    <p className="font-mono font-black text-3xl text-autoxium-blue">{fmt(results.rpm, 1)} <span className="text-base font-normal text-muted-foreground">rpm</span></p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="p-4 rounded-md border border-autoxium-blue bg-autoxium-blue/5">
                      <p className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground mb-1">Power</p>
                      <p className="font-mono font-black text-3xl text-autoxium-blue">{fmt(results.power.kw)} <span className="text-base font-normal text-muted-foreground">kW</span></p>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <div className="p-2.5 rounded-md border border-structure bg-muted/30">
                        <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground mb-1">W</p>
                        <p className="font-mono font-bold text-sm">{fmt(results.power.w, 0)}</p>
                      </div>
                      <div className="p-2.5 rounded-md border border-structure bg-muted/30">
                        <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground mb-1">HP</p>
                        <p className="font-mono font-bold text-sm">{fmt(results.power.hp)}</p>
                      </div>
                      <div className="p-2.5 rounded-md border border-structure bg-muted/30">
                        <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground mb-1">PS</p>
                        <p className="font-mono font-bold text-sm">{fmt(results.power.ps)}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="technical-card" data-testid="torque-power-rpm-formula">
            <CardHeader>
              <CardTitle className="font-chivo uppercase tracking-tight text-sm">Formula Reference</CardTitle>
              <CardDescription>All three quantities follow the same rotating-shaft equation; the active mode's rearrangement is highlighted.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {MODES.map((m) => (
                <div
                  key={m.id}
                  className={`flex items-center gap-2 p-2.5 rounded-md border font-mono text-xs ${m.id === mode ? 'border-autoxium-blue bg-autoxium-blue/5 text-autoxium-blue font-bold' : 'border-structure bg-muted/30 text-muted-foreground'}`}
                >
                  {m.id === mode && <Badge className="bg-autoxium-blue text-[9px] shrink-0">active</Badge>}
                  {m.formula}
                </div>
              ))}
              {results && (
                <p className="text-[11px] font-mono text-muted-foreground pt-1 border-t border-structure">
                  Current: {fmt(results.torque.nm)} N·m = {fmt(results.power.kw)} kW × 9550 / {fmt(results.rpm, 1)} rpm
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <RelationshipChart chartData={chartData} results={results} />
    </div>
  );
};

const RelationshipChart = ({ chartData, results }) => {
  const zoom = useChartZoom([0, 'dataMax']);
  return (
    <Card className="technical-card">
      <CardHeader>
        <CardTitle className="font-chivo uppercase tracking-tight text-sm flex items-center gap-2">
          <GaugeCircle className="h-4 w-4 text-autoxium-blue" /> Power-Torque-RPM Relationship
        </CardTitle>
        <CardDescription>
          Holding Power constant at the current operating point, Torque falls as RPM rises
          (Torque = Power × 9550 / RPM). The dashed line marks the current operating RPM.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {!results ? (
          <p className="text-sm text-muted-foreground py-12 text-center" data-testid="torque-power-rpm-chart-placeholder">
            Enter valid inputs to see the relationship chart.
          </p>
        ) : (
          <>
            <ChartZoomHeader isZoomed={zoom.isZoomed} onReset={zoom.resetZoom} testId="zoom-reset-torque-power-rpm" />
            <div className="h-96">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart
                  data={chartData} margin={{ top: REFERENCE_LABEL_TOP_MARGIN, right: 16, left: 8, bottom: 24 }}
                  onMouseDown={zoom.onMouseDown} onMouseMove={zoom.onMouseMove} onMouseUp={zoom.onMouseUp}
                >
                  <CartesianGrid stroke={CHART_GRID} vertical={false} />
                  <XAxis
                    dataKey="rpm" type="number" domain={zoom.xDomain} allowDataOverflow tick={AXIS_TICK_STYLE}
                    label={{ value: 'RPM', position: 'insideBottom', offset: -12, style: AXIS_LABEL_STYLE }}
                    tickFormatter={(v) => Math.round(v).toLocaleString()}
                  />
                  <YAxis
                    yAxisId="torque" tick={AXIS_TICK_STYLE} width={64}
                    label={{ value: 'Torque (Nm)', angle: -90, position: 'insideLeft', style: AXIS_LABEL_STYLE }}
                  />
                  <YAxis
                    yAxisId="power" orientation="right" tick={AXIS_TICK_STYLE} width={64}
                    label={{ value: 'Power (kW)', angle: 90, position: 'insideRight', style: AXIS_LABEL_STYLE }}
                  />
                  <RechartsTooltip
                    contentStyle={TOOLTIP_CONTENT_STYLE}
                    labelStyle={TOOLTIP_LABEL_STYLE}
                    formatter={(v, name) => [fmt(v, 2), name]}
                    labelFormatter={(v) => `${fmt(v, 0)} RPM`}
                  />
                  <Legend wrapperStyle={{ ...LEGEND_STYLE, paddingTop: 18 }} />
                  <ReferenceLine x={results.rpm} yAxisId="torque" stroke="#dc2626" strokeDasharray="4 4"
                    label={<ReferenceLineTopLabel text="Operating Point" color="#dc2626" anchor={referenceLabelAnchorFor(results.rpm, zoom.xDomain, chartData, 'rpm')} />} />
                  <Line yAxisId="torque" type="monotone" dataKey="torqueNm" name="Torque (Nm)" stroke={SERIES_COLORS[0]} {...LINE_PROPS} />
                  <Line yAxisId="power" type="monotone" dataKey="powerKw" name="Power (kW)" stroke={SERIES_COLORS[1]} {...LINE_PROPS} />
                  {zoom.isSelecting && (
                    <ReferenceArea yAxisId="torque" x1={zoom.refAreaLeft} x2={zoom.refAreaRight} stroke={CHART_AXIS} fill={CHART_AXIS} fillOpacity={0.15} />
                  )}
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </>
        )}
        <div className="flex items-start gap-1.5 text-xs text-muted-foreground bg-muted/50 rounded-md p-2.5 mt-3">
          <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
          This calculator provides a theoretical relationship between power, torque, and
          rotational speed. Actual engine/motor output can differ due to operating conditions,
          efficiency, control strategy, torque limits, and drivetrain losses.
        </div>
      </CardContent>
    </Card>
  );
};

export default TorquePowerRpmCalculator;
